import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Calendar, User, ArrowRight, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const BlogSection = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch blogs from public/data/blogs.json
    fetch('/data/blogs.json')
      .then(response => response.json())
      .then(data => {
        // Filter only active blogs
        const activeBlogs = data.filter(blog => blog.active);
        setBlogs(activeBlogs);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading blogs:', error);
        toast.error('Failed to load blog posts');
        setLoading(false);
      });
  }, []);

  const featuredPosts = blogs.filter(blog => blog.featured).slice(0, 2);
  const recentPosts = blogs.filter(blog => !blog.featured).slice(0, 3);

  if (loading) {
    return (
      <section id="blog" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-xl text-gray-600">Loading blog posts...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="blog" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="bg-[#e4e2de] text-[#000000] hover:bg-[#ce9b42] hover:text-white mb-4">
            Travel Insights
          </Badge>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Latest from Our Blog
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Tips, guides, and inspiration for your next adventure
          </p>
        </div>

        {/* Featured Posts */}
        {featuredPosts.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {featuredPosts.map((post) => (
              <Card
                key={post.id}
                className="group overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 bg-white"
              >
                {/* Image */}
                {post.imageUrl && (
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={post.imageUrl}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    
                    {/* Category Badge */}
                    <div className="absolute top-4 left-4">
                      <span className="bg-[#ef571b] text-white text-xs font-medium px-4 py-2 rounded-full">
                        {post.category}
                      </span>
                    </div>

                    {/* Featured Badge */}
                    <div className="absolute top-4 right-4">
                      <span className="bg-[#ffde59] text-black text-xs font-bold px-4 py-2 rounded-full">
                        FEATURED
                      </span>
                    </div>
                  </div>
                )}

                {/* Content */}
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-[#ef571b] transition-colors">
                    {post.title}
                  </h3>
                  
                  {/* Meta Info */}
                  <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-4 w-4" />
                      <span>{post.date}</span>
                    </div>
                    {post.author && (
                      <div className="flex items-center space-x-1">
                        <User className="h-4 w-4" />
                        <span>{post.author}</span>
                      </div>
                    )}
                  </div>

                  <p className="text-gray-600 mb-6 line-clamp-3">{post.excerpt}</p>

                  <Link to="/blog">
                    <Button className="bg-[#ef571b] hover:bg-[#d84915] text-white group/btn">
                      Read More
                      <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Recent Posts Grid */}
        {recentPosts.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {recentPosts.map((post) => (
              <Card
                key={post.id}
                className="group overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white"
              >
                {/* Image */}
                {post.imageUrl && (
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={post.imageUrl}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                    
                    {/* Category */}
                    <div className="absolute top-3 left-3">
                      <span className="bg-[#ef571b] text-white text-xs font-medium px-3 py-1 rounded-full">
                        {post.category}
                      </span>
                    </div>
                  </div>
                )}

                {/* Content */}
                <CardContent className="p-5">
                  <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-[#ef571b] transition-colors">
                    {post.title}
                  </h3>
                  
                  {/* Meta */}
                  <div className="flex items-center space-x-3 text-xs text-gray-500 mb-3">
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-3 w-3" />
                      <span>{post.date}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock className="h-3 w-3" />
                      <span>5 min read</span>
                    </div>
                  </div>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{post.excerpt}</p>

                  <Link to="/blog">
                    <Button 
                      variant="ghost" 
                      className="text-[#ef571b] hover:text-[#d84915] hover:bg-[#ef571b]/10 p-0 h-auto font-semibold group/btn"
                    >
                      Read Article
                      <ArrowRight className="ml-1 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* View All Button */}
        <div className="text-center">
          <Link to="/blog">
            <Button 
              size="lg"
              variant="outline"
              className="border-2 border-[#ef571b] text-[#ef571b] hover:bg-[#ef571b] hover:text-white px-8 py-6 text-lg"
            >
              View All Posts
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogSection;